import { useQuery } from "@tanstack/react-query";
import { useAuthReady } from "hooks/useAuthReady";
import { request } from "hooks/useOkoApi/useOkoApi";
import { normalizeGroupAssignments } from "hooks/useCurriculum/useCurriculum";
import { sessionCardInput } from "utils/sessionAssignment";
import { resolveGroupCardTitle } from "utils/groupCardTitle";

// What a session was working on, named the way the dashboard names it.
//
// The report's Focus line matches the group code card, so both read the same
// title logic in resolveGroupCardTitle rather than keeping two copies that can
// drift. That helper reads an assignment row and its bundle, which come from
// the classroom's current assignment — reconciled against the session's own
// snapshot first, so a group that has since been reassigned keeps the
// standards it actually ran.

const EMPTY_BUNDLES = [];

/**
 * The classroom's assignment bundles, shared across every session row on screen.
 *
 * Session History lists many sessions from the same handful of classrooms; one
 * query key per classroom keeps that to one request each.
 */
const useClassroomBundles = (classroomId) => {
  const authReady = useAuthReady();

  const { data } = useQuery({
    queryKey: ["classroom-bundles", classroomId],
    queryFn: () =>
      request(`curriculum/classrooms/${classroomId}/group-assignments`, { method: "GET" })
        .then(normalizeGroupAssignments)
        // A classroom with nothing assigned is an ordinary state, not a failure:
        // the session still names itself from its own snapshot.
        .catch((e) => {
          if (e?.status === 404) return { lessons: [] };
          throw e;
        }),
    enabled: Boolean(classroomId) && authReady,
    staleTime: 5 * 60 * 1000,
  });

  return data?.lessons || EMPTY_BUNDLES;
};

/**
 * The Focus line for one session.
 *
 * @param {object} session A session-history row: classroom_id, group_code,
 *   standard_prefix, standard_prefix_name, lesson_standard_prefixes.
 * @returns {{title: string, code: string, codeTooltip: string|null,
 *   countLabel: string|null, titledBy: string}} The resolved card title, or
 *   nulls when there is no session to name.
 */
export const useSessionFocus = (session) => {
  const bundles = useClassroomBundles(session?.classroom_id);

  if (!session) {
    return {
      title: null,
      code: null,
      label: null,
      codeTooltip: null,
      countLabel: null,
      titledBy: null,
    };
  }

  const { assignment, bundle } = sessionCardInput(session, bundles);
  const { title, code, codeTooltip, countLabel, titledBy } = resolveGroupCardTitle(
    assignment,
    bundle,
  );

  // The helper renders an em dash when it has nothing to name; report callers
  // want a falsy value so they can keep whatever they were showing before.
  const resolvedTitle = title === "—" ? null : title;
  const resolvedCode = code === "—" ? null : code;

  return {
    title: resolvedTitle,
    code: resolvedCode,
    // The two on one line, for a report that has room for a sentence rather
    // than a card's stacked code and title. A standard read without its code
    // cannot be looked up, and the code alone does not say what it covers.
    //
    // With nothing to name it, the helper titles the card with the code
    // itself — which must read as one code, not as a code explained by itself.
    label: (resolvedTitle === resolvedCode
      ? resolvedCode
      : [resolvedCode, resolvedTitle].filter(Boolean).join(" - ")) || null,
    codeTooltip,
    countLabel,
    titledBy,
  };
};
